/* eslint-disable react-hooks/rules-of-hooks */
"use client";

import Image from "next/image";
import Link from "next/link";

import { Popover, PopoverTrigger } from "@/components/atoms/popover";
import { usePathname } from "@/i18n/routing";
import { kebabCase } from "@/utils";
import { PopoverContent } from "@radix-ui/react-popover";
import { ChevronDown } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { LazyInquiryForm } from "./LazyInquiryForm";
import {
  useGetPremierResidenceHeaderQuery,
  useGetPremierResidenceNavigationQuery,
  useGetPremierResidenceMainNavigationQuery,
  useGetInquiryFormQuery,
  useGetPremierResidenceListQuery,
} from "@/hooks/usePremierResidence/usePremierResidenceQueries";
type SelectedItem = {
  order: number;
  documentId: string;
};
const Header = () => {
  const pathname = usePathname();
  if (!pathname.includes("/premier-residence")) {
    return;
  }
  const locale = useLocale();
  const { data: premierResidenceHeader } = useGetPremierResidenceHeaderQuery();
  const { data: premierResidenceHeaderNavigation } =
    useGetPremierResidenceNavigationQuery();
  const { data: premierResidenceMainNavigation } =
    useGetPremierResidenceMainNavigationQuery();
  const { data: inquiryFormQuery } = useGetInquiryFormQuery();
  const { data: premierResidenceList } = useGetPremierResidenceListQuery();
  /** Track scroll position to control header animation (logo scaling) */
  const [isAtTop, setIsAtTop] = useState(true);

  /** Controls open/close state for residence dropdown */
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  /**
   * Create navigation list from CMS data while filtering out hidden items
   * and mapping document info with kebab-cased links
   */
  const navList = useMemo(() => {
    return premierResidenceHeaderNavigation?.sharedDatasourceNavigationDatasource?.menu_items
      .filter((i) => i?.show !== false)
      .map((i) => ({
        documentId: i?.documentId || "",
        menu_title: i?.menu_title || "",
        href: kebabCase(i?.item_name || ""),
      }));
  }, [
    premierResidenceHeaderNavigation?.sharedDatasourceNavigationDatasource
      ?.menu_items,
  ]);

  /** Prepare the order and display of header navigation items */
  const displayMenuItems =
    premierResidenceHeaderNavigation?.sharedDatasourceNavigationDatasource
      ?.display_menu_items;

  /**
   * Match selected items (defined in CMS order)
   * with corresponding navigation data and maintain display order
   */
  const selectedNavList = (
    Array.isArray(displayMenuItems) ? (displayMenuItems as SelectedItem[]) : []
  )
    .filter(
      (selectedItem: SelectedItem) =>
        selectedItem?.documentId &&
        navList?.some((page) => page?.documentId === selectedItem.documentId),
    )
    .sort((a: SelectedItem, b: SelectedItem) => a.order - b.order)
    .map((selectedItem: SelectedItem) => {
      const pageData = navList?.find(
        (page) => page?.documentId === selectedItem.documentId,
      );
      return pageData;
    })
    .filter(Boolean);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /** Regex check to determine if on a specific residence page */

  // eslint-disable-next-line no-useless-escape
  const isShowMainNavigation = /^\/premier-residence\/[^\/]+/.test(pathname);

  /** Retrieve main navigation items for residence subpages */
  const premierResidenceMainMenuItems =
    premierResidenceMainNavigation?.sharedDatasourceNavigationDatasource
      ?.menu_items;
  const menuOrder = ["Overview", "Rooms", "Price", "Gallery", "Location"];

  const sortedVisibleMenuItems = premierResidenceMainMenuItems
    ?.filter(
      (item) =>
        item?.item_name?.toLowerCase() === "price" || item?.show === true,
    )
    ?.sort(
      (a, b) =>
        menuOrder.indexOf(a?.item_name || "") -
        menuOrder.indexOf(b?.item_name || ""),
    );

  const headerPremierResidenceLogo =
    premierResidenceHeader?.headerDatasource?.logo_image?.url;

  if (pathname.includes("/premier-residence"))
    return (
      /** Render header only for Premier Residence pages */
      <header className="fixed top-[55px] right-0 left-0 z-20 w-full shadow-md" data-testid="premier-residence-header">
        <nav className="bg-primary-950">
          <div className="wrapper flex h-[60px] items-center justify-start sm:h-[80px] sm:justify-between">
            {/** Logo that scales and repositions based on scroll position */}

            <div
              className={`${
                isAtTop
                  ? "relative bottom-[-2rem] scale-[0.8] sm:bottom-[-3rem] sm:scale-100"
                  : "aspect-square h-full"
              } min-h-[60px] min-w-[80px] flex-shrink-0`}
            >
              {premierResidenceHeader?.headerDatasource?.logo_image?.url !==
                undefined && (
                <Link
                  href={
                    pathname.includes("/serviceapartment")
                      ? "#"
                      : `/${locale}/premier-residence`
                  }
                >
                  <Image
                    src={headerPremierResidenceLogo || ""}
                    alt={
                      premierResidenceHeader?.headerDatasource?.logo_image
                        ?.alternativeText || ""
                    }
                    aria-label={
                      premierResidenceHeader?.headerDatasource?.logo_image
                        ?.caption || ""
                    }
                    width={120}
                    height={40}
                    loading="lazy"
                    className="max-sm:w-[80px]"
                  />
                </Link>
              )}
            </div>
            {/** Navigation + Inquiry section */}
            <div className="flex items-center max-md:w-full min-sm:min-w-[400px]">
              <div
                className={`flex-1 overflow-hidden ${isShowMainNavigation ? "max-[846px]:max-w-[calc(100%-150px)] max-[500px]:!max-w-[calc(100%-210px)]" : "max-[500px]:max-w-[calc(100%-130px)] max-[321px]:!max-w-[calc(100%-170px)]"}`}
              >
                <div className="hide-scrollbar flex flex-shrink-0 items-center gap-5 overflow-x-auto px-2 whitespace-nowrap sm:gap-8">
                  <Popover open={isSelectOpen} onOpenChange={setIsSelectOpen}>
                    <PopoverTrigger asChild>
                      <div className="flex cursor-pointer items-center space-x-1 text-sm text-white hover:text-gray-300">
                        <span>レジデンス一覧</span>
                        <ChevronDown size={16} />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="mt-2 rounded-lg border border-[var(--fill-gray-light)] bg-white px-3 py-2 shadow-lg">
                      <div className="flex flex-col items-center gap-2">
                        {selectedNavList?.map((item) => (
                          <Link
                            className="text-primary-950 text-sm font-[var(--font-medium)]"
                            key={item?.documentId}
                            href={`/${locale}/premier-residence/${item?.href}`}
                            onClick={() => setIsSelectOpen(false)}
                          >
                            {item?.menu_title}
                          </Link>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {/** Render either main navigation or fallback link */}
                  {isShowMainNavigation ? (
                    sortedVisibleMenuItems?.map((item) => (
                      <Link
                        key={item?.documentId}
                        href={item?.menu_link?.metadata?.url}
                        className="block text-sm text-white hover:text-gray-300"
                      >
                        {item?.menu_title}
                      </Link>
                    ))
                  ) : (
                    <Link
                      className="block text-sm text-white hover:text-gray-300"
                      href="#location"
                    >
                      ロケーション
                    </Link>
                  )}
                </div>
              </div>
              <LazyInquiryForm
                inquiryFormQuery={inquiryFormQuery}
                premierResidenceList={premierResidenceList}
                triggerButton={{
                  label:
                    premierResidenceHeader?.headerDatasource?.inquiry_label,
                  showIcon: true,
                  className: "hidden min-[846px]:block ml-6 ",
                }}
              />
            </div>
            <LazyInquiryForm
              inquiryFormQuery={inquiryFormQuery}
              premierResidenceList={premierResidenceList}
              triggerButton={{
                label: premierResidenceHeader?.headerDatasource?.inquiry_label,
                showIcon: true,
                className: "hidden max-[846px]:block absolute right-2",
              }}
            />
          </div>
        </nav>
      </header>
    );
};
export { Header as PremierResidenceHeader };
